import { useEffect, useSyncExternalStore } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './api';
import { syncNow } from './SyncEngine';
import * as PendingQueue from './PendingQueue';

// ── Query Keys ────────────────────────────────────────────────
// `allTransactions` intentionally has NO filter in the key — the full
// unfiltered list is the canonical cache; filtered views are computed
// client-side in the components that consume it.
export const QUERY_KEYS = {
  dashboardSummary: (dashboardId) => ['dashboard_stats', dashboardId],
  allTransactions:  (dashboardId) => ['transactions', dashboardId],
  accounts:         (dashboardId) => ['accounts', dashboardId],
  consolidatedAccounts: (dashboardId) => ['consolidated_accounts', dashboardId],
  splitGroups:      (dashboardId) => ['split_groups', dashboardId],
  profileSettings:   () => ['profile_settings'],
};

// ── Fetchers ──────────────────────────────────────────────────
// These are fallbacks used only when the IndexedDB cache is empty for a
// dashboard (first device-load) and the sync hasn't populated it yet.
const fetchDashboardSummary = async (dashboardId) => {
  if (!dashboardId) return null;
  const { data } = await api.get(`/api/transactions/stats?dashboard_id=${dashboardId}`);
  return data;
};

const fetchAllTransactions = async (dashboardId) => {
  if (!dashboardId) return [];
  // Fall-back bulk fetch — SyncEngine handles this at login, but keeping the
  // fetcher lets React Query transparently refill a cold cache.
  const { data } = await api.get(`/api/transactions?dashboard_id=${dashboardId}&limit=1000`);
  return data?.transactions || [];
};

const fetchAccounts = async (dashboardId) => {
  if (!dashboardId) return [];
  const { data } = await api.get(`/api/accounts?dashboard_id=${dashboardId}`);
  return data || [];
};

const fetchConsolidatedAccounts = async (dashboardId) => {
  if (!dashboardId) return [];
  const { data } = await api.get(`/api/consolidated-accounts?dashboard_id=${dashboardId}`);
  return data || [];
};

const fetchSplitGroups = async (dashboardId) => {
  if (!dashboardId) return [];
  const { data } = await api.get(`/api/split-groups?dashboard_id=${dashboardId}`);
  return data || [];
};

const fetchProfileSettings = async () => {
  const { data } = await api.get('/api/profile/settings');
  return data || {};
};

// ── Sync Engine: incremental delta ─────────────────────────────

/**
 * useBudgetSync is the "Central Nervous System" of the app.
 *
 *   1. Runs an incremental /api/sync on mount, window-focus and network
 *      reconnect (only the delta since the last successful sync).
 *   2. Subscribes to Supabase Realtime for the full synced-table set so
 *      open tabs stay live without extra requests.
 */
export const useBudgetSync = (dashboardId) => {
  const queryClient = useQueryClient();

  // ── Incremental delta sync ──
  useEffect(() => {
    if (!dashboardId) return undefined;

    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      syncNow(queryClient, dashboardId).catch(() => { /* handled in syncNow */ });
    };

    run(); // initial sync on mount

    const onFocus = () => { if (document.visibilityState === 'visible') run(); };
    const onOnline = () => run();

    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [dashboardId, queryClient]);

  return {
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', dashboardId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard_stats', dashboardId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', dashboardId] });
      queryClient.invalidateQueries({ queryKey: ['consolidated_accounts', dashboardId] });
    },
    syncNow: () => syncNow(queryClient, dashboardId),
  };
};

// ── Standard Hooks ────────────────────────────────────────────

export const useDashboardSummary = (dashboardId) => {
  return useQuery({
    queryKey: QUERY_KEYS.dashboardSummary(dashboardId),
    queryFn: () => fetchDashboardSummary(dashboardId),
    enabled: !!dashboardId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useAllTransactions = (dashboardId) => {
  return useQuery({
    queryKey: QUERY_KEYS.allTransactions(dashboardId),
    queryFn: () => fetchAllTransactions(dashboardId),
    enabled: !!dashboardId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useAccounts = (dashboardId) => {
  return useQuery({
    queryKey: QUERY_KEYS.accounts(dashboardId),
    queryFn: () => fetchAccounts(dashboardId),
    enabled: !!dashboardId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useConsolidatedAccounts = (dashboardId) => {
  return useQuery({
    queryKey: QUERY_KEYS.consolidatedAccounts(dashboardId),
    queryFn: () => fetchConsolidatedAccounts(dashboardId),
    enabled: !!dashboardId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useSplitGroups = (dashboardId) => {
  return useQuery({
    queryKey: QUERY_KEYS.splitGroups(dashboardId),
    queryFn: () => fetchSplitGroups(dashboardId),
    enabled: !!dashboardId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useProfileSettings = () => {
  return useQuery({
    queryKey: QUERY_KEYS.profileSettings(),
    queryFn: fetchProfileSettings,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const usePrefetchTransactions = (dashboardId) => {
  const queryClient = useQueryClient();
  return () => {
    if (!dashboardId) return;
    queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.allTransactions(dashboardId),
      queryFn: () => fetchAllTransactions(dashboardId),
    });
    queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.dashboardSummary(dashboardId),
      queryFn: () => fetchDashboardSummary(dashboardId),
    });
  };
};

// ── Add transaction: enqueue + background upload ──────────────
//
// The mutation no longer hits the network directly. It writes the new tx to
// the IndexedDB-backed PendingQueue and returns immediately. A separate
// processor (mounted by `usePendingQueueProcessor`) drains the queue
// whenever the network is available. This makes Add work offline and on
// flaky connections — the row appears in the UI right away and uploads as
// soon as the device can reach the API.
export const useAddTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // The actual upload happens in the queue processor. Here we just
    // enqueue and add the optimistic row; mutationFn is a no-op so the
    // mutation resolves immediately and the user gets instant feedback
    // (works identically online and offline).
    mutationFn: async (_newTx) => {
      // Trigger a process pass — does nothing if offline.
      PendingQueue.process().catch(() => { /* surfaced via queue state */ });
      return { queued: true };
    },
    onMutate: async (newTx) => {
      const dbId = newTx.dashboard_id;
      const localId = PendingQueue.makeLocalId();

      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.allTransactions(dbId) });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.dashboardSummary(dbId) });

      const previousTransactions = queryClient.getQueryData(QUERY_KEYS.allTransactions(dbId));

      queryClient.setQueryData(QUERY_KEYS.allTransactions(dbId), (old = []) => [
        {
          ...newTx,
          id: localId,
          _localId: localId,
          _pending: true,
          created_at: new Date().toISOString(),
        },
        ...old,
      ]);

      // Persist to the IDB outbox. The processor will drain it on the
      // next tick (or whenever the network returns).
      await PendingQueue.enqueue({ localId, payload: newTx, dashboardId: dbId });

      return { previousTransactions, dbId, localId };
    },
    onError: (err, newTx, context) => {
      // Enqueue itself almost never throws — but if it does, fall back to
      // the prior cache so the user can retry.
      if (context?.previousTransactions) {
        queryClient.setQueryData(QUERY_KEYS.allTransactions(context.dbId), context.previousTransactions);
      }
    },
    onSettled: (data, error, variables, context) => {
      // Don't call syncNow here — the processor invokes it after a real
      // upload succeeds. Calling it now would race against the optimistic
      // local row and either flicker or pull a still-empty server delta.
      if (context?.dbId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboardSummary(context.dbId) });
      }
    },
  });
};

// ── Pending-queue processor ────────────────────────────────────
//
// Mounts the upload pipeline on the React tree. Configures PendingQueue
// once with an uploader bound to `api.post('/api/transactions', ...)` and
// reconciles the React Query cache after each successful upload (swap the
// optimistic `local-…` row for the server-authoritative one and pull a
// fresh delta for derived state).
//
// Triggers a process pass on:
//   - mount,
//   - the browser `online` event,
//   - tab focus,
//   - a slow periodic timer (every 30s) to retry stuck items.
export const usePendingQueueProcessor = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    PendingQueue.configure({
      uploader: async (payload) => {
        const { data } = await api.post('/api/transactions', payload);
        return data;
      },
      onSuccess: (item, serverRow) => {
        const dbId = item.dashboardId || serverRow?.dashboard_id || item.payload?.dashboard_id;
        if (!dbId) return;

        // Swap the optimistic row for the server row (preserve list order).
        queryClient.setQueryData(QUERY_KEYS.allTransactions(dbId), (old = []) => {
          const idx = old.findIndex((t) => t._localId === item.localId || t.id === item.localId);
          if (idx === -1) {
            // Local row already gone — let realtime/sync deliver the real one.
            return old;
          }
          // If the realtime channel already inserted the server row, just
          // drop the local row to avoid a duplicate.
          if (old.some((t) => t.id === serverRow?.id && t._localId !== item.localId)) {
            return old.filter((t) => t._localId !== item.localId);
          }
          const copy = old.slice();
          copy[idx] = { ...serverRow };
          return copy;
        });

        // Pull the server delta so any joined fields (accounts, tags) and
        // derived stats catch up.
        syncNow(queryClient, dbId).catch(() => { /* handled in syncNow */ });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboardSummary(dbId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts(dbId) });
      },
      onFailure: () => {
        // No-op: the queue itself records the failure; the indicator UI
        // surfaces it. We retry on the next online/focus tick.
      },
    });

    // Initial drain — covers reload-with-leftover-items.
    PendingQueue.ready().then(() => PendingQueue.process()).catch(() => {});

    const tick = () => PendingQueue.process().catch(() => {});
    const onOnline = () => tick();
    const onFocus = () => { if (document.visibilityState === 'visible') tick(); };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onFocus);
    const interval = setInterval(tick, 30000);

    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onFocus);
      clearInterval(interval);
    };
  }, [queryClient]);
};

// ── Subscribe to the queue from React ──────────────────────────
const subscribeQueue = (cb) => PendingQueue.subscribe(cb);
const getQueueSnapshot = () => PendingQueue.getAll();

export const usePendingTransactions = () => {
  return useSyncExternalStore(subscribeQueue, getQueueSnapshot, getQueueSnapshot);
};

export const retryPendingUploads = () => PendingQueue.process();
export const removePendingUpload = (localId) => PendingQueue.remove(localId);
